import React, { useState } from "react";
import { Globe, Search, ServerCog } from "lucide-react";
import { useAdminRoutingCorridors, useAdminSettings } from "../../hooks/useAdmin";
import { PERMISSIONS, usePermissions } from "../../hooks/usePermissions";
import { AdminLlmKeys } from "./AdminLlmKeys";
import { format, messages as t } from "./AdminSettings.messages";

export function AdminSettings() {
  const { configuration, isLoading, error } = useAdminSettings();
  const { can } = usePermissions();
  const canReadRoutes = can(PERMISSIONS.ROUTING_READ);

  return (
    <div className="space-y-8 max-w-6xl text-left">
      {/* PAGE HEADER */}
      <div>
        <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
          Live Configuration
        </h2>
        <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
          Providers & Corridors
        </h1>
        <p className="text-xs text-slate-400 mt-2">
          Provider switches come from server environment configuration.
        </p>
      </div>

      {/* GLOBAL ERROR */}
      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          Could not load the current configuration.
        </div>
      )}

      {/* RUNTIME READINESS */}
      <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
        <SectionTitle
          icon={ServerCog}
          title="Runtime readiness"
          subtitle="Safe, non-secret values from /health/configuration"
        />

        {isLoading ? (
          <Loading />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Readiness
              label="Environment"
              value={configuration?.environment || "unknown"}
              ready
            />
            <Readiness
              label="Encryption"
              value={configuration?.data_encryption_configured ? "Configured" : "Missing"}
              ready={configuration?.data_encryption_configured}
            />
            <Readiness
              label="Mock mode"
              value={configuration?.provider_mock_mode ? "Enabled" : "Disabled"}
              ready={!configuration?.provider_mock_mode}
            />
            <Readiness
              label="CORS origins"
              value={configuration?.cors_origin_count ?? 0}
              ready={(configuration?.cors_origin_count ?? 0) > 0}
            />
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(configuration?.providers || {}).map(([provider, enabled]) => (
            <div key={provider} className="rounded-2xl border border-slate-100 p-4">
              <p className="text-xs font-semibold text-[#0A2540] capitalize">
                {humanize(provider)}
              </p>
              <p className={`text-xs mt-1 font-medium ${enabled ? "text-emerald-600" : "text-slate-400"}`}>
                {enabled ? "Enabled" : "Disabled"}
              </p>
            </div>
          ))}
        </div>
      </section>

      <AdminLlmKeys />

      {canReadRoutes && <RoutesSection />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ROUTES (read-only: computed from each
   provider's legs on the server)
───────────────────────────────────────────── */

// Reasons that apply to a whole corridor rather than to one onramp/offramp pair.
const CORRIDOR_REJECTIONS = new Set([
  "corridor_not_offered",
  "no_onramp_leg",
  "no_offramp_leg",
]);

function RoutesSection() {
  const { corridors, isLoadingCorridors, corridorsError } = useAdminRoutingCorridors();
  const [searchQuery, setSearchQuery] = useState("");

  const query = searchQuery.toLowerCase().trim();
  const filteredCorridors = query
    ? corridors.filter((corridor) =>
        corridorSearchTerms(corridor).some((term) => term.includes(query))
      )
    : corridors;

  return (
    <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
      <SectionTitle
        icon={Globe}
        title={t["admin_settings.routes.title"]}
        subtitle={t["admin_settings.routes.subtitle"]}
      />

      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t["admin_settings.routes.search"]}
          className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 text-xs text-[#0A2540] outline-none transition focus:bg-white focus:border-[#0A4730]/40 focus:ring-2 focus:ring-[#0A4730]/5 placeholder:text-slate-400"
        />
      </div>

      {isLoadingCorridors ? (
        <Loading text={t["admin_settings.routes.loading"]} />
      ) : corridorsError ? (
        <p className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          {t["admin_settings.routes.load_failed"]}
        </p>
      ) : filteredCorridors.length ? (
        <div className="divide-y divide-slate-100">
          {filteredCorridors.map((corridor) => (
            <CorridorRoutes key={corridorKey(corridor)} corridor={corridor} />
          ))}
        </div>
      ) : (
        <Empty
          text={
            corridors.length
              ? t["admin_settings.routes.no_match"]
              : t["admin_settings.routes.empty"]
          }
        />
      )}
    </section>
  );
}

function CorridorRoutes({ corridor }) {
  const [preferred, ...alternatives] = corridor.routes ?? [];
  const rejections = corridor.rejections ?? [];
  const heading = format("admin_settings.routes.corridor", corridor);

  return (
    <div role="group" aria-label={heading} className="py-4 space-y-3">
      <p className="text-sm font-semibold text-[#0A2540]">
        {heading}
      </p>

      {preferred ? (
        <>
          <RouteLine route={preferred} isPreferred />
          {alternatives.length > 0 && (
            <div className="space-y-2">
              <p className="text-[9px] uppercase tracking-[0.16em] font-semibold text-[#0A2540]/50">
                {t["admin_settings.routes.alternatives"]}
              </p>
              {alternatives.map((route) => (
                <RouteLine key={route.id} route={route} />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-2xl bg-red-50/60 border border-red-100 p-4 space-y-2">
          <p className="text-xs font-semibold text-red-600">
            {t["admin_settings.routes.no_route"]}
          </p>
          <ul className="space-y-1 text-xs text-[#0A2540]/70">
            {rejections.map((rejection, index) => (
              <li key={`${rejection.reason}-${index}`}>
                {describeRejection(rejection, corridor)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RouteLine({ route, isPreferred = false }) {
  const description = `${describeOnramp(route.onramp)} → ${describeOfframp(route.offramp)}`;

  return (
    <div
      role="group"
      aria-label={description}
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-2xl border border-slate-100 p-4"
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold text-[#0A2540] break-words">
          {description}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {format("admin_settings.routes.settlement", { asset: route.settlement_asset })}
        </p>
      </div>
      {isPreferred && (
        <span className="shrink-0 self-start sm:self-center rounded-full bg-[#C0F6B7]/40 px-3 py-1 text-[10px] font-semibold text-[#0A4730]">
          {t["admin_settings.routes.preferred"]}
        </span>
      )}
    </div>
  );
}

/** "bridge · gbp.fps": the provider and the rail it collects on. */
function describeOnramp(leg) {
  return [humanize(leg.provider), leg.rail || humanize(leg.channel || "")]
    .filter(Boolean)
    .join(" · ");
}

/** "zuba · mobile money · MPESA_KE": the provider, channel and network it pays out on. */
function describeOfframp(leg) {
  return [humanize(leg.provider), humanize(leg.channel || ""), leg.network]
    .filter(Boolean)
    .join(" · ");
}

function describeRejection(rejection, corridor) {
  const key = `admin_settings.routes.rejection.${rejection.reason}`;
  const hasPair = Boolean(rejection.onramp && rejection.offramp);
  const values = {
    ...corridor,
    reason: humanize(rejection.reason),
    onramp: hasPair ? describeOnramp(rejection.onramp) : "",
    offramp: hasPair ? describeOfframp(rejection.offramp) : "",
  };
  const isKnown = key in t && (hasPair || CORRIDOR_REJECTIONS.has(rejection.reason));
  if (isKnown) return format(key, values);
  return format(
    hasPair
      ? "admin_settings.routes.rejection.unknown"
      : "admin_settings.routes.rejection.unknown_corridor",
    values
  );
}

function corridorKey(corridor) {
  return [
    corridor.sender_country,
    corridor.send_currency,
    corridor.recipient_country,
    corridor.receive_currency,
  ].join("-");
}

/** Countries, currencies and every provider on a route or a rejected pair. */
function corridorSearchTerms(corridor) {
  const legs = [...(corridor.routes ?? []), ...(corridor.rejections ?? [])].flatMap(
    (candidate) => [candidate.onramp, candidate.offramp]
  );
  return [
    corridor.sender_country,
    corridor.send_currency,
    corridor.recipient_country,
    corridor.receive_currency,
    ...legs.map((leg) => leg?.provider),
  ]
    .filter(Boolean)
    .map((term) => String(term).toLowerCase());
}

/* ─────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────── */

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-slate-100 min-w-0">
      <div className="w-10 h-10 rounded-2xl bg-[#C0F6B7]/40 text-[#0A4730] grid place-items-center shrink-0">
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-serif font-semibold text-[#0A4730]">
          {title}
        </h3>
        <p className="text-xs text-slate-400">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   EXISTING UI HELPERS
───────────────────────────────────────────── */

function Readiness({ label, value, ready }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <p className="text-[9px] uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <p className={`mt-2 text-sm font-semibold ${ready ? "text-[#0A4730]" : "text-red-500"}`}>
        {value}
      </p>
    </div>
  );
}

function Loading({ text = "Loading configuration…" }) {
  return (
    <p className="text-sm text-slate-400 py-6">
      {text}
    </p>
  );
}

function Empty({ text }) {
  return (
    <p className="text-sm text-slate-400 py-6">
      {text}
    </p>
  );
}

function humanize(value = "") {
  return String(value).replaceAll("_", " ");
}